import { useEffect, useMemo } from 'react';
import { Spinner } from '@iwoca/orion';
import clsx from 'clsx';
import { useReviewOffer } from 'components/ReviewOffer/ReviewOffer.context';
import { TColumnData } from 'components/SummaryCard/SummaryCard.helpers';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useNumberFormat } from 'hooks/useNumberFormat';
import {
  useGetAllowedWithdrawalFee,
  useGetWithdrawalFeeRate,
} from 'lendingApi/lendingApiHooks';
import styles from '../../SummaryCard.module.css';
import { getOfferSelectionStatus } from '../SummaryCardColumn.helpers';

type TProps = {
  applicationId: string;
  isLoadingPayments: boolean;
  offer: TColumnData & { offer_id: string; adjustable_fee: boolean };
  withdrawalFee: number;
  setWithdrawalFee: (withdrawalFee: number) => void;
  setFormattedMonthlyRepayments: (formattedMonthlyRepayments: string) => void;
};

export const MonthlyPayments = ({
  applicationId,
  offer,
  isLoadingPayments,
  withdrawalFee,
  setWithdrawalFee,
  setFormattedMonthlyRepayments,
}: TProps) => {
  const { introducerId } = useIntroducerContext();
  const { formatCurrency } = useNumberFormat();
  const {
    offerDecision,
    selectedOfferId,
    isSingleOffer,
    hasInterestOnlyOffer,
    setSelectedOfferMonthlyRepayment,
    setHasInterestOnlyOffer,
  } = useReviewOffer();

  const { data: withdrawalFeeRate, isLoading: feeRateLoading } =
    useGetWithdrawalFeeRate(offer.offer_id);

  const { data: allowedWithdrawalFees, isLoading: withdrawalFeeLoading } =
    useGetAllowedWithdrawalFee(
      introducerId as string,
      applicationId,
      offer.offer_id,
      offer.product_type as string,
    );

  const { offerIsSelected, offerNotSelected } = getOfferSelectionStatus(
    offerDecision,
    selectedOfferId,
    offer.offer_id,
  );

  // Get the selected commission value object to access the new monthly payment
  const selectedFeeRate = useMemo(
    () =>
      allowedWithdrawalFees?.data?.allowed_fee_rates.filter((fee) => {
        return fee.fee_rate === withdrawalFee;
      }),
    [allowedWithdrawalFees, withdrawalFee],
  );

  useEffect(() => {
    if (!withdrawalFeeRate?.data?.withdrawal_fee_rate) return;
    if (feeRateLoading) return;
    if (!offer.adjustable_fee) return;

    setWithdrawalFee(withdrawalFeeRate.data.withdrawal_fee_rate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeRateLoading, offer.product_type, withdrawalFeeRate]);

  const monthlyAmount =
    offer.adjustable_fee && selectedFeeRate
      ? selectedFeeRate?.[0]?.average_monthly_repayment
      : allowedWithdrawalFees?.data?.allowed_fee_rates[0]
          .average_monthly_repayment;

  const interestOnlyRepayments =
    allowedWithdrawalFees?.data?.interest_only_repayments;

  useEffect(() => {
    if (interestOnlyRepayments && hasInterestOnlyOffer) {
      setFormattedMonthlyRepayments(
        `${formatCurrency(
          interestOnlyRepayments.average_interest_only,
        )} (1 to 3 months avg.), ${formatCurrency(
          interestOnlyRepayments.average_outstanding,
        )} (4 to 15 months)`,
      );
    } else {
      setFormattedMonthlyRepayments(formatCurrency(monthlyAmount));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeeRate, hasInterestOnlyOffer]);

  useEffect(() => {
    if ((offerIsSelected || isSingleOffer) && monthlyAmount) {
      setSelectedOfferMonthlyRepayment(monthlyAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerIsSelected, monthlyAmount, isSingleOffer]);

  useEffect(() => {
    if (interestOnlyRepayments) {
      setHasInterestOnlyOffer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interestOnlyRepayments]);

  return (
    <div className={styles.monthlyRepaymentsContainer}>
      {(isLoadingPayments || withdrawalFeeLoading) && (
        <div
          className={clsx(styles.loaderContainer, {
            [styles.selectedValue]: offerIsSelected,
            [styles.unselectedValue]: offerNotSelected,
          })}
          data-testid="monthly-payments-spinner"
        >
          <Spinner />
        </div>
      )}
      {!isLoadingPayments && allowedWithdrawalFees && (
        <div
          className={clsx(styles.value, {
            [styles.selectedValue]: offerIsSelected,
            [styles.unselectedValue]: offerNotSelected,
          })}
        >
          {interestOnlyRepayments && hasInterestOnlyOffer ? (
            <div>
              <div className={styles.interestOnlyPayment}>
                <span>
                  {formatCurrency(interestOnlyRepayments.average_interest_only)}
                </span>
                <span>(1 to 3 months avg.)</span>
              </div>
              <div className={styles.interestOnlyPayment}>
                <span>
                  {formatCurrency(interestOnlyRepayments.average_outstanding)}
                </span>
                <span>(4 to 15 months)</span>
              </div>
            </div>
          ) : (
            formatCurrency(monthlyAmount)
          )}
        </div>
      )}
    </div>
  );
};
